import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { api, type ToolEntry } from "../lib/api.ts";

/**
 * Read-only summary of tools available to a single agent — the granular view
 * underneath the skill-level chips above. For each tool the agent has access
 * to (via assigned skill or by being a built-in), shows current state:
 *
 *   - allow   = explicit pre-approval in permissions.allow
 *   - deny    = explicit block in permissions.deny
 *   - default = no rule (Claude Code's default-ask)
 *
 * Each chip links to /tools/:fullKey where the matrix lets the owner change
 * state. Read-only here on purpose — keeps the agent page from duplicating
 * the matrix UI.
 */
export function AgentToolsSummary({ agentKey }: { agentKey: string }) {
	const [tools, setTools] = useState<ToolEntry[] | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		api
			.listTools()
			.then((r) => setTools(r.tools))
			.catch((err) => setError((err as Error).message));
	}, []);

	const relevant = useMemo(() => {
		if (!tools) return null;
		const out: Array<{ tool: ToolEntry; allowed: boolean; denied: boolean }> = [];
		for (const t of tools) {
			// Orchestrator tools aren't reachable from project-agents — skip entirely.
			if (t.source.kind === "orchestrator") continue;
			const state = t.agents.find((a) => a.key === agentKey);
			if (!state) continue;
			// Only show tools the agent actually has access to (granted via skill
			// or built-in always-on). Skill-level placeholders for plugins also
			// shown when granted, with permissionRule = null.
			if (!state.granted) continue;
			out.push({ tool: t, allowed: state.allowed, denied: state.denied });
		}
		out.sort((a, b) => {
			const ka = sourceKey(a.tool);
			const kb = sourceKey(b.tool);
			if (ka !== kb) return ka.localeCompare(kb);
			return a.tool.name.localeCompare(b.tool.name);
		});
		return out;
	}, [tools, agentKey]);

	const grouped = useMemo(() => {
		if (!relevant) return null;
		const byGroup = new Map<string, Array<{ tool: ToolEntry; allowed: boolean; denied: boolean }>>();
		for (const entry of relevant) {
			const k = sourceKey(entry.tool);
			const arr = byGroup.get(k) ?? [];
			arr.push(entry);
			byGroup.set(k, arr);
		}
		return byGroup;
	}, [relevant]);

	return (
		<section className="mt-6 border border-zinc-800 rounded-lg bg-zinc-900/30">
			<header className="px-4 py-3 border-b border-zinc-800">
				<h2 className="text-sm font-semibold text-zinc-100">Tools</h2>
				<div className="text-[11px] text-zinc-500 mt-0.5">
					Granular view of every individual tool this agent can call. Click any chip to change
					allow / deny state.
				</div>
			</header>

			<div className="p-4 space-y-4">
				{error && <div className="text-xs text-red-300">{error}</div>}
				{tools === null && !error && (
					<div className="text-xs text-zinc-500">Loading…</div>
				)}
				{relevant !== null && relevant.length === 0 && (
					<div className="text-xs text-zinc-500 italic">
						No tools available. Assign a skill above to grant access to its tools.
					</div>
				)}
				{grouped !== null &&
					[...grouped.entries()].map(([groupLabel, entries]) => (
						<div key={groupLabel}>
							<div className="text-[11px] uppercase tracking-wide text-zinc-500 mb-2">
								{groupLabel}
							</div>
							<div className="flex flex-wrap gap-1.5">
								{entries.map(({ tool, allowed, denied }) => (
									<ToolChip
										key={tool.fullKey}
										tool={tool}
										allowed={allowed}
										denied={denied}
									/>
								))}
							</div>
						</div>
					))}
			</div>
		</section>
	);
}

function sourceKey(t: ToolEntry): string {
	if (t.source.kind === "mcp") return `MCP · ${t.source.serverDisplayName}`;
	if (t.source.kind === "builtin") return "Built-in Claude Code";
	if (t.source.kind === "plugin") return "Plugin / marketplace";
	return "Other";
}

function ToolChip({
	tool,
	allowed,
	denied,
}: {
	tool: ToolEntry;
	allowed: boolean;
	denied: boolean;
}) {
	const state = denied ? "deny" : allowed ? "allow" : "default";
	const cls =
		state === "deny"
			? "bg-red-500/15 border-red-500/40 text-red-200 hover:border-red-500/70"
			: state === "allow"
				? "bg-emerald-500/15 border-emerald-500/40 text-emerald-200 hover:border-emerald-500/70"
				: "border-zinc-700 text-zinc-300 hover:border-zinc-500";
	return (
		<Link
			to={`/tools/${encodeURIComponent(tool.fullKey)}`}
			className={`text-[11px] px-2 py-1 rounded border font-mono inline-flex items-center gap-1.5 ${cls}`}
			title={tool.description ?? tool.fullKey}
		>
			<span>{tool.name}</span>
			{state !== "default" && (
				<span className="text-[9px] uppercase tracking-wide opacity-75">{state}</span>
			)}
		</Link>
	);
}
